import { useCallback, useEffect, useRef, useState } from "react";

import { useQueryParams } from "../../../../../hooks/useQueryParams";
import useService from "../../../../../hooks/useService";
import ApiRequestErrorHandler from "../../../../global/ApiRequestErrorHandler";
import { ACTION_TYPE } from "../utils/strings";

export default function useFollowUpHistory({ caseId }) {
  const followUpHistoryRepository = useService("repositories/actionable-items-repository");
  const { filters } = useQueryParams();
  const revalidateHistoryFlag = filters.get("rvFollowUpHistory");
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState([]);
  const [showMore, setShowMore] = useState(false);
  const [limit, setLimit] = useState(null);
  const [offset, setOffset] = useState(null);
  const allowedKeys = Object.values(ACTION_TYPE).map(({ key }) => Number(key));
  const actionTypeIds = allowedKeys.join(",");
  const isActiveRef = useRef(true);

  useEffect(() => {
    return () => {
      isActiveRef.current = false;
    };
  }, []);

  useEffect(() => {
    refetchData();
  }, [caseId]);

  useEffect(() => {
    if (!revalidateHistoryFlag) {
      return;
    }
    refetchData();
  }, [revalidateHistoryFlag]);

  async function refetchData() {
    if (!isActiveRef.current) {
      return;
    }

    setData([]);
    setLimit(null);
    setOffset(null);
    await fetchData({ limit: null, offset: null, prevData: [] });
  }

  const fetchData = useCallback(
    async ({
      limit: limitOverride = limit,
      offset: offsetOverride = offset,
      prevData = data,
    } = {}) => {
      if (!isActiveRef.current) {
        return;
      }

      setIsLoading(true);

      try {
        const queryParams = {
          on_case: caseId,
          action_type__in: actionTypeIds,
          status: "done",
          ordering: "-completed_at",
          ...(limitOverride && { limit: limitOverride }),
          ...(offsetOverride && { offset: offsetOverride }),
        };

        const response = await followUpHistoryRepository.query(queryParams);
        const nextPageURL = await followUpHistoryRepository.getNextPage();

        if (!isActiveRef.current) {
          return;
        }

        if (nextPageURL) {
          const url = new URL(nextPageURL);
          setShowMore(true);
          setLimit(Number(url.searchParams.get("limit")) || limitOverride);
          setOffset(Number(url.searchParams.get("offset")) || offsetOverride);
        } else {
          setShowMore(false);
        }

        setData([...prevData, ...response]);
      } catch (error) {
        console.error("Error fetching follow-up history:", error);
        return ApiRequestErrorHandler(error);
      } finally {
        if (isActiveRef.current) {
          setIsLoading(false);
        }
      }
    },
    [caseId, followUpHistoryRepository, limit, offset]
  );

  return {
    isLoading,
    data,
    showMore,
    fetchData,
  };
}
